import {columnNamesTypeTableCollapse} from "@oceantech/oceantech-ui";
import clsx from "clsx";
import React, {ChangeEvent, FC, useState} from "react";
import {Form} from "react-bootstrap";
import {getColumnsAlignByType} from "../../../utils/AppFunction";
import {SELECTION_MODE, TABLE_FIELD, TYPE} from "../../../utils/Constant";
import {fieldGroupByLevel, fieldParentProps} from "./OctTableCollapse";
import {formatDateToDDMMYYYY} from "../../../utils/FormatUtils";

export interface TableRowProps {
  row: any;
  index: number;
  name?: string;
  nameParent?: string;
  columns: columnNamesTypeTableCollapse[];
  isCollapsed?: boolean;
  nameChildren: string;
  sorting?: boolean;
  isParent?: boolean;
  toggleCollapse?: () => void;
  handleCheckBox: (
    event: ChangeEvent<HTMLInputElement>,
    data: any,
    parentIndex: number
  ) => void;
  toggleRowSelection: (row: any, parentIndex: number) => void;
  handleSingleSelect: (row: any, parentIndex: number) => void;
  selectionMode?: (typeof SELECTION_MODE)[keyof typeof SELECTION_MODE];
  handleCheckBoxAll: (event: ChangeEvent<HTMLInputElement>, data: any) => void;
  parentIndex: number;
  isSelect?: boolean;
  disabledSelect?: boolean;
  handleDoubleClick?: (row: any) => void;
  isRenderCollapseIcon?: boolean;
  level: number;
  path?: number[];
  isLevel?: boolean;
  isOneLevelCheckBox?: boolean;
  customFieldParent?: fieldParentProps[];
  fieldGroupByLevel?: fieldGroupByLevel[];
  handleSetShowCheckBox?: (row: any) => boolean;
}
const OctTableCollapseRow: FC<TableRowProps> = (props) => {
  const {
    row,
    columns,
    index,
    nameParent,
    nameChildren,
    selectionMode,
    toggleRowSelection,
    handleSingleSelect,
    parentIndex,
    isSelect,
    disabledSelect,
    handleDoubleClick,
    isRenderCollapseIcon,
    level,
    path = [],
    isLevel,
    isOneLevelCheckBox,
    customFieldParent,
    fieldGroupByLevel,
    handleSetShowCheckBox,
  } = props;

  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const isParent = row?.[nameChildren] && row?.[nameChildren]?.length > 0;

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const stt = [...path, index + 1].join(".");
  const excludedFields = [TABLE_FIELD.STT, TABLE_FIELD.ACTION, TABLE_FIELD.TT];

  const convertValue = (value: fieldParentProps, useLabel = true) => {
    const raw = row[value.field];
    const val = value.type === TYPE.DATE ? formatDateToDDMMYYYY(raw) : raw;
    return useLabel && value.label && val ? `${value.label}: ${val}` : val ?? "";
  };

  const getFieldValues = (
      customFields?: fieldParentProps[],
      groupedFields?: fieldGroupByLevel[],
      useDirectRow = false,
      level?: number
  ) => {
    let fields: fieldParentProps[] = [];

    // Ưu tiên group theo level nếu có
    const group = (level != null && groupedFields)
        ? groupedFields.find(g => g.level === level)
        : undefined;

    if (group?.field?.length) {
      fields = group.field;
    }
    // Nếu không có group thì dùng customFields nếu có
    else if (customFields?.length) {
      fields = customFields;
    }
    // Nếu không có gì thì fallback từ columns
    else {
      fields = columns
          .filter(col => !excludedFields.includes(col.field))
          .map(col => ({ field: col.field }));
    }

    // Chỉ dùng label nếu có ít nhất 1 label
    const useLabel = fields.some(f => !!f.label);

    return fields
        .map(x =>
            useDirectRow
                ? convertValue(x, useLabel)
                : columns.find(col => col.field === x.field)?.field && row[x.field]
        )
        .filter(v => v !== null && v !== undefined && v !== "");
  };

  const txt = getFieldValues(
      columns
          .filter(col => !excludedFields.includes(col.field))
          .map(col => ({ field: col.field, label: col?.name }))
  );

  const parentTxt = getFieldValues(
      props.customFieldParent,
      props.fieldGroupByLevel,
      true,
      props.level
  );

  const txtMerge = (((isOneLevelCheckBox && level ===0) || row?.[nameChildren]?.length)
          ? (props.customFieldParent || props.fieldGroupByLevel ? parentTxt : txt)
          : []
  ).join(" - ");

  const isShowCheckBox = handleSetShowCheckBox ? handleSetShowCheckBox?.(row) : false;

  return (
    <>
      {/* Row name */}
      {nameParent && row[nameParent] && isParent && (
        <tr
          key={index}
          className={clsx(
            "bg-white row-name position-sticky start-0 border border-bottom spaces h-30",
            isParent ? "cursor-pointer" : ""
          )}
          onClick={toggleCollapse}
        >
          <td
            colSpan={1}
            className={clsx(
              "cell-action position-sticky start-0 bg-white z-index-1",
              isCollapsed && "chevron-rotate-down"
            )}
            onClick={toggleCollapse}
          >
            <span className="">
              {isParent && <i className="fs-5 bi bi-caret-right-fill" />}
            </span>
          </td>
          <td colSpan={columns?.length}>
            <div className="flex flex-middle">
              <span className="spaces mr-8">
                {isSelect && (
                  <Form.Check
                    className="checkBox"
                    type="checkbox"
                    onChange={(event) =>
                      !disabledSelect && props?.handleCheckBoxAll(event, index)
                    }
                    checked={row?.isParentChecked || false}
                    disabled={disabledSelect}
                  />
                )}
              </span>
              <span className="position-sticky spaces left-38px fw-bold">
                {`${row[nameParent]} (${row[nameChildren]?.length})`}
              </span>
            </div>
          </td>
        </tr>
      )}

      {/* Row content */}
      {!(nameParent && row[nameParent]) && (
        <tr
          className={clsx(
            `custom-${nameChildren} table-collapse-border-bottom cursor-pointer`,
            row.isChecked && "selected-row"
          )}
          // onClick={() => {
          //   if (selectionMode === SELECTION_MODE.MULTI) {
          //     !disabledSelect && toggleRowSelection(row, parentIndex);
          //   }
          //   if (selectionMode === SELECTION_MODE.SINGLE) {
          //     !disabledSelect && handleSingleSelect(row, parentIndex);
          //   }
          // }}
          onDoubleClick={() => handleDoubleClick && handleDoubleClick(row)}
          style={{
            background: `rgba(0, 0, 0, ${level / 40})`,
            paddingLeft: `${level * 15}px`,
            height: level ? "25px" : "30px",
          }}
        >
          {/*checkbox and icon cell*/}
          {(isParent || isRenderCollapseIcon || isSelect) && (
            <td
              className={clsx(
                "cell-action position-sticky start-0 py-0 w-0",
                isCollapsed && "chevron-rotate-down"
              )}
            >
              <div className="flex justify-content-center">
                {selectionMode === SELECTION_MODE.MULTI && isSelect && (
                  <div className="checkbox-wrapper">
                    <Form.Check
                      className={`checkBox ${
                        isShowCheckBox ? ""
                          : isOneLevelCheckBox ? "d-none" : ""
                      }`}
                      type="checkbox"
                      checked={row.isChecked}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        !disabledSelect &&
                        props?.handleCheckBox(event, row, parentIndex)
                      }
                      disabled={disabledSelect}
                    />
                  </div>
                )}
                {selectionMode === SELECTION_MODE.SINGLE && (
                  <Form.Check
                    className="customs-form-check__radio__table"
                    type="radio"
                    checked={row.isChecked}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      !disabledSelect &&
                      props?.handleCheckBox(event, row, parentIndex)
                    }
                    disabled={disabledSelect}
                  />
                )}
                {isParent && (
                    <span
                        className={clsx("btn-icon-md")}
                        onClick={toggleCollapse}
                    >
                    {isCollapsed ? (
                        <i className="fs-5 bi bi-caret-down-fill" />
                    ) : (
                        <i className="fs-5 bi bi-caret-right-fill" />
                    )}
                  </span>
                )}
              </div>
            </td>
          )}
          {/*other cell*/}
          {columns?.map((column, indexTd) => {
            const textAlign = {
              textAlign: getColumnsAlignByType(column),
            } as React.CSSProperties;
            const style = {
              ...textAlign,
              ...column?.cellStyle,
            };
            const isMergeParentText = txtMerge && isLevel && !excludedFields.includes(column?.field);
            const isSetFrontWeight = ((isOneLevelCheckBox && level === 0) || row?.[nameChildren]?.length) ? "fw-600" : null;
            if (isMergeParentText && column?.isCollapse) {
              return (
                  <td
                      className={`py-0 ${isSetFrontWeight} td-child`}
                      key={indexTd}
                      style={style}
                      colSpan={isMergeParentText ? columns?.length : 0}
                  >
                    <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: style?.justifyContent || excludedFields.includes(column?.field) ? "center" : "unset",
                          paddingLeft: `${level * 15}px`, // Thụt lề theo cấp
                        }}
                    >
                      {column?.field === TABLE_FIELD.STT
                          ? stt
                          : isMergeParentText
                              ? txtMerge
                              : column.render
                                  ? column.render(row, index)
                                  : row[column?.field]}
                    </div>
                  </td>)
            } else {
            return column?.isCollapse ? (!isMergeParentText &&
              <td
                className={`py-0 ${isSetFrontWeight} td-child`}
                key={indexTd}
                style={style}
              >
                <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: style?.justifyContent || excludedFields.includes(column?.field) ? "center" : "unset",
                      paddingLeft: `${level * 15}px`, // Thụt lề theo cấp
                    }}
                >
                  {column?.field === TABLE_FIELD.STT
                      ? stt
                      : column.render
                          ? column.render(row, index)
                          : row[column?.field]}
                </div>
              </td>
            ) : (!isMergeParentText &&
              <td
                className={`py-0 ${isSetFrontWeight} td-child`}
                key={indexTd}
                style={style}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: style?.justifyContent || excludedFields.includes(column?.field) ? "center" : "unset",
                  }}
                >
                  {column?.field === TABLE_FIELD.STT
                      ? stt
                      : column.render
                          ? column.render(row, index)
                          : row[column?.field]}
                </div>
              </td>
            );}
          })}
        </tr>
      )}

      {/* Nếu có [nameChildren] thì tiếp tục map [nameChildren] */}
      {isCollapsed &&
        row?.[nameChildren] &&
        row?.[nameChildren]?.length > 0 &&
        row?.[nameChildren].map((child: any, i: number) => (
          <OctTableCollapseRow
            handleSingleSelect={handleSingleSelect}
            toggleRowSelection={toggleRowSelection}
            key={i}
            nameChildren={nameChildren}
            columns={columns}
            row={child}
            index={i}
            handleCheckBox={props?.handleCheckBox}
            selectionMode={selectionMode}
            handleCheckBoxAll={props?.handleCheckBoxAll}
            parentIndex={index}
            isSelect={isSelect}
            disabledSelect={disabledSelect}
            handleDoubleClick={handleDoubleClick}
            isRenderCollapseIcon={isRenderCollapseIcon}
            isCollapsed={isCollapsed}
            level={level + 1} //adds 1 level of depth each time render the children
            path={[...path, index + 1]}
            isLevel={isLevel}
            customFieldParent={customFieldParent}
            fieldGroupByLevel={fieldGroupByLevel}
            handleSetShowCheckBox={handleSetShowCheckBox}
            isOneLevelCheckBox={isOneLevelCheckBox}
          />
        ))}
    </>
  );
};
export default OctTableCollapseRow;
